import React, { useState, useCallback, useMemo, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase';
import { approveMaterialRequest, approveSmartRequest, rejectMaterialRequest } from '../../services/materialRequestService';
import { formatDateTime, formatRelativeAge } from '../../utils/formatters';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ViewToggle from '../../components/common/ViewToggle';
import useViewToggle from '../../hooks/useViewToggle';
import { parseRequestItems } from '../../utils/materialSplitPreview';
import { useMaterialSplitPreview } from '../../hooks/useMaterialSplitPreview';
import {
  MaterialSplitBadges,
  MaterialSplitItemRow,
  MaterialSplitStockWarning,
  MaterialSimpleBadge,
  MaterialSimpleItemRow,
} from '../../components/materials/MaterialSplitPreview';
import './MaterialRequestsInbox.css';

const fetchPendingRequests = async () => {
  const { data, error } = await supabase
    .from('v_material_requests_with_items')
    .select(`
      *,
      project:projects(id, project_number, title),
      requester:profiles!material_requests_requested_by_fkey(id, full_name),
      inventory_item:inventory(id, name, sku, unit)
    `)
    .in('status', ['pending'])
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/**
 * Los cuatro filtros de la barra. Cada uno es un predicado sobre el REPARTO ya
 * calculado (`summarize().counts`), no sobre `fulfillment_type` — que desde
 * #384 no decide nada (ver el encabezado de utils/materialSplitPreview.js).
 *
 * El eje es uno solo y es el que le importa a quien aprueba: qué sale de bodega
 * y qué hay que salir a comprar. "1 ítem" vs "1 de compra" —lo que se mostraba
 * antes— eran dos ejes distintos disfrazados de uno.
 */
const FILTROS = [
  { id: 'todas', label: 'Pendientes', match: () => true },
  { id: 'compra', label: 'Con compra', match: (c) => c.purchase > 0 || c.partial > 0 },
  { id: 'sindato', label: 'Sin dato', match: (c) => c.unknown > 0 },
  {
    id: 'bodega',
    label: 'Solo de bodega',
    match: (c) => c.stock > 0 && !c.purchase && !c.partial && !c.unknown,
  },
];

/** Todo el texto por el que se puede buscar una solicitud. */
const searchHaystack = (req, items) => [
  req.project?.project_number,
  req.project?.title,
  req.requester?.full_name,
  req.notes,
  req.source_input_text,
  req.inventory_item?.name,
  // Una externa de un ítem (MR-2026-0022, "Cemento mixto") no tiene nombre en
  // ningún otro campo: sin esto, buscar "cemento" no la encontraba.
  req.external_description,
  req.external_supplier,
  ...items.map((it) => it?.item_description || it?.name),
].filter(Boolean).join(' ').toLowerCase();

/**
 * #29 — el markup de una solicitud vive UNA sola vez.
 *
 * Esta pantalla ahora tiene dos vistas (fila y tarjeta) del MISMO dato. Cada
 * pedazo que las dos muestran se extrae acá: si alguien agrega un campo a la
 * fila y no a la tarjeta, la tarjeta deja de "mostrar todo lo que muestra la
 * fila" y nadie se entera. Compartir el componente lo hace estructural.
 *
 * Viven en ESTE archivo a propósito: `materialSplitPreview.test.js` exige que
 * `MaterialSplitBadges` / `MaterialSplitItemRow` / `MaterialSplitStockWarning`
 * sigan siendo importados literalmente por MaterialRequestsInbox.js — mover el
 * detalle a un archivo aparte pone ese gate en rojo (#513).
 */

function ToggleSolicitud({ req, abierta, panelId, onToggle }) {
  return (
    <button
      type="button"
      className="mri-toggle"
      aria-expanded={abierta}
      aria-controls={panelId}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
    >
      <i
        className={`bi ${abierta ? 'bi-chevron-down' : 'bi-chevron-right'} mri-flecha`}
        aria-hidden="true"
      />
      <span className="mri-proy-titulo">
        {/* El titulo del proyecto es el protagonista; el numero es un chip (#356). */}
        <span className="mri-proy-num">{req.project?.project_number || 's/n'}</span>
        {req.project?.title || 'Proyecto sin titulo'}
      </span>
    </button>
  );
}

function QuienSolicitud({ req }) {
  return (
    <span className="mri-quien">
      {req.requester?.full_name || '—'}
      {/* Hora + edad relativa: en una cola FIFO la fecha sola no alcanza (#569). */}
      <span className="mri-cuando">
        {formatDateTime(req.created_at)} · {formatRelativeAge(req.created_at)}
      </span>
    </span>
  );
}

function RepartosSolicitud({ req, items, summary, stockLoading }) {
  return (
    <span className="mri-repartos">
      {items.length > 0 ? (
        <MaterialSplitBadges summary={summary} loading={stockLoading} />
      ) : (
        /* Solicitud de un ítem (#783): el chip nombra el material y qué pasa
           al aprobar. Antes era un literal fijo para una externa, para una de
           catálogo y para una que no identificaba nada. */
        <MaterialSimpleBadge req={req} />
      )}
    </span>
  );
}

/**
 * Aprobar / Rechazar. NO hay kebab acá y es deliberado: el estandar del repo
 * pide kebab con Ver/Editar/Eliminar, pero en esta bandeja las acciones de fila
 * SON el flujo principal (aprobar la cola), no operaciones secundarias sobre un
 * registro. Esconderlas detras de dos clicks es exactamente el costo que el
 * kebab existe para evitar en las OTRAS listas. Desvio declarado del estandar.
 */
function AccionesSolicitud({ req, isProcessing, onApprove, onReject }) {
  return (
    <span className="mri-acciones">
      <button
        type="button"
        className="mri-btn mri-btn--ok"
        disabled={isProcessing}
        onClick={(e) => { e.stopPropagation(); onApprove(req); }}
      >
        {isProcessing
          ? <Spinner size="sm" role="status" aria-label="Procesando" />
          : <><i className="bi bi-check-lg" aria-hidden="true" />Aprobar</>}
      </button>
      <button
        type="button"
        className="mri-btn mri-btn--no"
        disabled={isProcessing}
        onClick={(e) => { e.stopPropagation(); onReject(req); }}
      >
        <i className="bi bi-x-lg" aria-hidden="true" />Rechazar
      </button>
    </span>
  );
}

function DetalleSolicitud({ req, items, summary, stockLoading, panelId }) {
  return (
    <div className="mri-detalle" id={panelId}>
      {/* El dictado del tecnico, citado: es de donde salio la lista. */}
      {req.source_input_text && (
        <p className="mri-dictado">
          “{req.source_input_text.substring(0, 200)}
          {req.source_input_text.length > 200 ? '…' : ''}”
        </p>
      )}
      {/* #513 — reparto real por item, no badge binario */}
      {items.length > 0 && (
        <div className="mri-items">
          {summary.plans.map((plan, j) => (
            <MaterialSplitItemRow key={plan.item?.id || j} plan={plan} loading={stockLoading} />
          ))}
        </div>
      )}
      {/* Solicitud simple (un solo material, sin lista de items). Externa, de
          catálogo o sin material: los tres casos se dibujan con el componente
          compartido con el supervisor (#783). Antes sólo se dibujaba el de
          catálogo y una externa (MR-2026-0022) abría un panel VACÍO. */}
      {items.length === 0 && (
        <div className="mri-items">
          <MaterialSimpleItemRow req={req} />
        </div>
      )}
      {req.notes && <p className="mri-nota"><strong>Nota:</strong> {req.notes}</p>}
    </div>
  );
}

/** Modo TABLA: el grid `.mri-fila` de #569, sin tocar. */
function FilaSolicitud({ fl, abierta, isProcessing, stockLoading, panelId, onToggle, onApprove, onReject }) {
  const { req, items, summary } = fl;
  return (
    <div className={`mri-sol${abierta ? ' mri-sol--abierta' : ''}`}>
      <div className="mri-fila" onClick={onToggle}>
        <span className="mri-proy">
          <ToggleSolicitud req={req} abierta={abierta} panelId={panelId} onToggle={onToggle} />
        </span>
        <QuienSolicitud req={req} />
        <RepartosSolicitud req={req} items={items} summary={summary} stockLoading={stockLoading} />
        <AccionesSolicitud req={req} isProcessing={isProcessing} onApprove={onApprove} onReject={onReject} />
      </div>
      {abierta && (
        <DetalleSolicitud req={req} items={items} summary={summary} stockLoading={stockLoading} panelId={panelId} />
      )}
    </div>
  );
}

/**
 * Modo TARJETA (#29). Muestra exactamente lo mismo que la fila —los cuatro
 * bloques son los MISMOS componentes— apilado, con las acciones en un pie que
 * queda fuera del area clickeable del cuerpo.
 */
function TarjetaSolicitud({ fl, abierta, isProcessing, stockLoading, panelId, onToggle, onApprove, onReject }) {
  const { req, items, summary } = fl;
  return (
    <div className={`mri-card${abierta ? ' mri-card--abierta' : ''}`}>
      <div className="mri-card-cuerpo" onClick={onToggle}>
        <ToggleSolicitud req={req} abierta={abierta} panelId={panelId} onToggle={onToggle} />
        <QuienSolicitud req={req} />
        <RepartosSolicitud req={req} items={items} summary={summary} stockLoading={stockLoading} />
      </div>
      {abierta && (
        <DetalleSolicitud req={req} items={items} summary={summary} stockLoading={stockLoading} panelId={panelId} />
      )}
      <div className="mri-card-pie">
        <AccionesSolicitud req={req} isProcessing={isProcessing} onApprove={onApprove} onReject={onReject} />
      </div>
    </div>
  );
}

export default function MaterialRequestsInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [processingId, setProcessingId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState('');
  const [filtro, setFiltro] = useState('todas');
  const panelBaseId = useId();
  // #29 — card en <768px, tabla en >=768px, sin persistencia. Un solo `openId`
  // sirve a las dos vistas: cambiar de modo con una solicitud abierta la deja abierta.
  const { viewMode, setViewMode, isCardView } = useViewToggle();

  const { data: requests = [], isLoading, error } = useQuery({
    queryKey: ['material-requests-inbox'],
    queryFn: fetchPendingRequests,
    staleTime: 30_000,
  });

  // #513 — el preview tiene que contar con el MISMO reparto que la aprobación.
  // Una sola query de stock para toda la tanda; el resumen sale de
  // `planMaterialSplit`, no de `fulfillment_type`.
  const {
    isLoading: stockLoading,
    error: stockError,
    summarize,
  } = useMaterialSplitPreview(requests);

  // Una sola pasada: parseo + reparto + texto buscable por solicitud. Los
  // filtros y el buscador leen de acá, así que cambiar de chip no recalcula
  // ningún split.
  const filas = useMemo(() => requests.map((req) => {
    const items = parseRequestItems(req);
    return {
      req,
      items,
      summary: summarize(items),
      haystack: searchHaystack(req, items),
    };
  }), [requests, summarize]);

  const conteoPorFiltro = useMemo(() => {
    const out = {};
    for (const f of FILTROS) out[f.id] = filas.filter((fl) => f.match(fl.summary.counts)).length;
    return out;
  }, [filas]);

  const totales = useMemo(() => filas.reduce((acc, fl) => {
    const c = fl.summary.counts;
    acc.stock += c.stock; acc.partial += c.partial;
    acc.purchase += c.purchase; acc.unknown += c.unknown;
    return acc;
  }, { stock: 0, partial: 0, purchase: 0, unknown: 0 }), [filas]);

  const visibles = useMemo(() => {
    const activo = FILTROS.find((f) => f.id === filtro) || FILTROS[0];
    const q = query.trim().toLowerCase();
    return filas.filter((fl) => activo.match(fl.summary.counts) && (!q || fl.haystack.includes(q)));
  }, [filas, filtro, query]);

  const handleApprove = useCallback(async (req) => {
    setProcessingId(req.id);
    setFeedback(null);
    try {
      const items = parseRequestItems(req);
      let result;

      if (items.length > 0) {
        result = await approveSmartRequest(req.id, user.id);
      } else {
        result = await approveMaterialRequest(req.id, user.id);
      }

      if (result.error) throw result.error;

      // Dos servicios, dos formas: `approveSmartRequest` devuelve
      // `data.{warnings,notices}`; `approveMaterialRequest` devolvía `warning`
      // en singular en la raíz y esta página leía `data.warnings`, así que la
      // advertencia del camino de un ítem se perdía por el nombre del campo.
      const warnings = result.data?.warnings ?? result.warnings ?? [];
      // #384 — `notices` cuenta el REPARTO (qué se retira de bodega y qué se
      // compra). No son fallas, pero callarlas dejaría al admin creyendo que
      // "stock consumido" quiere decir que ya está todo resuelto, cuando parte
      // del ítem todavía hay que ir a comprarla.
      const notices = result.data?.notices ?? result.notices ?? [];
      const splitCount = result.data?.split_items_count || 0;
      // "stock consumido" sólo si el servicio lo dice (status 'consumed').
      // Medido en prod (MR-2026-0022): una externa quedaba 'approved' sin
      // consumir nada ni generar compra y este banner decía lo contrario.
      let base;
      if (splitCount > 0) {
        base = `Solicitud aprobada con reparto: ${notices.join(' ')}`;
      } else if (result.data?.status === 'consumed') {
        base = 'Solicitud aprobada y stock consumido.';
      } else {
        base = ['Solicitud aprobada.', ...notices].join(' ');
      }
      setFeedback({
        type: warnings?.length ? 'warning' : 'success',
        msg: warnings?.length
          ? `Aprobado con advertencias: ${warnings.join('; ')}${notices.length ? ` — ${notices.join(' ')}` : ''}`
          : base,
      });
      queryClient.invalidateQueries({ queryKey: ['material-requests-inbox'] });
      // Aprobar consume stock: el preview de las solicitudes que quedan se
      // vuelve viejo en el mismo instante.
      queryClient.invalidateQueries({ queryKey: ['material-split-stock'] });
    } catch (err) {
      setFeedback({ type: 'danger', msg: err.message || 'Error al aprobar' });
    } finally {
      setProcessingId(null);
    }
  }, [user?.id, queryClient]);

  const handleReject = useCallback(async (req) => {
    const reason = window.prompt('Motivo de rechazo:');
    if (!reason?.trim()) return;

    setProcessingId(req.id);
    setFeedback(null);
    try {
      const { error: err } = await rejectMaterialRequest(req.id, user.id, reason);
      if (err) throw err;
      setFeedback({ type: 'success', msg: 'Solicitud rechazada.' });
      queryClient.invalidateQueries({ queryKey: ['material-requests-inbox'] });
    } catch (err) {
      setFeedback({ type: 'danger', msg: err.message || 'Error al rechazar' });
    } finally {
      setProcessingId(null);
    }
  }, [user?.id, queryClient]);

  // El spinner pelado no tiene nombre accesible ni dice que se esta cargando.
  // Mismo patron que ProjectsUnifiedView / ProjectsTable: rol de estado +
  // texto para lector de pantalla, y un rotulo visible que nombra QUE esta
  // cargando. (Decia "ProjectList.js", un archivo muerto que webpack nunca
  // empaqueto y que se borro en L4 — ver serviceResultDeadErrorUI.test.js.)
  //
  // NOTA sobre el patron `unwrapServiceResult`: esta pantalla NO lo necesita.
  // Su `queryFn` es `fetchPendingRequests`, que consulta supabase directo y
  // hace `if (error) throw error`, asi que el `if (error)` de abajo SI es
  // alcanzable. Medido antes de tocarla; queda fuera de la tanda a proposito.
  //
  // #569 — estos dos early-return renderizan DENTRO de la variante `--work`.
  // Antes salian fuera de <DashboardLayout> por completo, asi que el spinner y
  // el error se pintaban sobre el fondo gris del body en vez de sobre la
  // pantalla. Meterlos adentro tambien los deja bajo la tinta oscura: el
  // spinner blanco de la regla vieja (DashboardLayout.css L508) ya no aplica.
  if (isLoading) {
    return (
      <DashboardLayout variant="work">
        <Container className="py-4 text-center">
          <Spinner role="status" variant="primary">
            <span className="visually-hidden">Cargando solicitudes de material...</span>
          </Spinner>
          <p className="mt-2 mb-0">Cargando solicitudes de material...</p>
        </Container>
      </DashboardLayout>
    );
  }
  if (error) {
    return (
      <DashboardLayout variant="work">
        <Container className="py-4"><Alert variant="danger">{error.message}</Alert></Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout variant="work">
      <div className="mri">
        <div className="mri-barra">
          <h1>Solicitudes de materiales</h1>
          {requests.length > 0 && <span className="mri-cuenta">{requests.length}</span>}
          <span className="mri-barra-meta">
            {stockLoading
              ? 'Calculando reparto…'
              : [
                totales.stock && `${totales.stock} de bodega`,
                totales.partial && `${totales.partial} ${totales.partial === 1 ? 'parcial' : 'parciales'}`,
                totales.purchase && `${totales.purchase} de compra`,
                totales.unknown && `${totales.unknown} sin dato`,
              ].filter(Boolean).join(' · ') || 'Sin ítems por repartir'}
          </span>
        </div>

        <div className="mri-toolbar">
          <input
            className="mri-buscador"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por proyecto, solicitante o material…"
            aria-label="Buscar solicitudes"
          />
          {FILTROS.map((f) => (
            <button
              key={f.id}
              type="button"
              className="mri-chip"
              aria-pressed={filtro === f.id}
              onClick={() => setFiltro(f.id)}
            >
              {f.label} <span className="n">{conteoPorFiltro[f.id]}</span>
            </button>
          ))}
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <button
            type="button"
            className="mri-btn mri-btn--nueva"
            onClick={() => navigate('/admin/solicitudes-materiales/nueva')}
          >
            <i className="bi bi-magic" aria-hidden="true" />Nueva solicitud
          </button>
        </div>

        {feedback && (
          <Alert
            className="mri-feedback"
            variant={feedback.type}
            dismissible
            onClose={() => setFeedback(null)}
          >
            {feedback.msg}
          </Alert>
        )}

        <div className="mri-feedback"><MaterialSplitStockWarning error={stockError} /></div>

        {!isCardView && (
          <div className="mri-cols" aria-hidden="true">
            <span>Proyecto</span>
            <span>Solicitante</span>
            <span>Reparto al aprobar</span>
            <span className="mri-col-accion">Acción</span>
          </div>
        )}

        {/* #29 — DESVIO DECLARADO DEL ESTANDAR: esta lista NO lleva kebab.
            El estandar ("UI List View Standards", CLAUDE.md) pide un kebab con
            Ver/Editar/Eliminar por fila/tarjeta. Acá las acciones son Aprobar/Rechazar,
            que son el trabajo de la pantalla, no operaciones secundarias sobre un
            registro. No hay "Ver" (el detalle se expande) ni "Editar"/"Eliminar"
            (rechazar NO es borrar: escribe motivo y estado). Se dejan visibles en las DOS vistas. */}
        {visibles.length === 0 ? (
          <div className="mri-lista">
            <p className="mri-vacio">
              <i className="bi bi-inbox fs-3 d-block mb-2" aria-hidden="true" />
              {requests.length === 0
                ? 'No hay solicitudes pendientes.'
                : 'Ninguna solicitud pendiente coincide con este filtro.'}
            </p>
          </div>
        ) : (
          <div className={isCardView ? 'mri-cards' : 'mri-lista'}>
            {visibles.map((fl) => {
              const abierta = openId === fl.req.id;
              const panelId = `${panelBaseId}-${fl.req.id}`;
              const comunes = {
                fl,
                abierta,
                isProcessing: processingId === fl.req.id,
                stockLoading,
                panelId,
                onToggle: () => setOpenId(abierta ? null : fl.req.id),
                onApprove: handleApprove,
                onReject: handleReject,
              };
              return isCardView
                ? <TarjetaSolicitud key={fl.req.id} {...comunes} />
                : <FilaSolicitud key={fl.req.id} {...comunes} />;
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
